<script>
import apiClient from '@services/ApiClient';
import CompanyTree from '@components/CreditCheck/CompanyTree.vue';

export default {
    name: 'BisnodeViewer',
    components: {
        CompanyTree,
    },
    props: {
        id: {
            type: [Number, String],
            required: false,
        },
        registrationNumber: {
            type: String,
            required: false,
        },
    },
    data: () => ({
        busy_loading: false,
        cc: null,
        q: '',
    }),
    mounted() {
        this.getCreditCheck();
    },
    computed: {
        companyName() {
            return this.cc.raw.companyInformation.companyName.registeredName.name;
        },
        currentRating() {
            return (this.cc.raw.risk.creditRatings && this.cc.raw.risk.creditRatings.currentCreditRating ? this.cc.raw.risk.creditRatings.currentCreditRating : null);
        },
        keyFiguresSorted() {
            const kf = this.cc.raw.finance.financialStatements.keyFigures || [];
            return kf.slice().sort((a, b) => new Date(b.closingPeriod.year, (b.closingPeriod.month || 1) - 1, b.closingPeriod.day || 1) - new Date(a.closingPeriod.year, (a.closingPeriod.month || 1) - 1, a.closingPeriod.day || 1));
        },
        historicalCreditRatings() {
            const ratings = {};
            this.cc.raw.risk.creditRatings.historicalCreditRatings.forEach((r) => {
                ratings[this.fmDate(r.date)] = r;
            });
            this.cc.raw.risk.ratingCreditLimits.historicalRatingCreditLimits.forEach((r) => {
                const key = this.fmDate(r.date);
                if (!ratings[key]) {
                    ratings[key] = r;
                } else {
                    ratings[key].amount = r.amount;
                }
            });

            return Object.values(ratings).sort((a, b) => this.fmDate(b.date) - this.fmDate(a.date));
        },
        filteredEvents() {
            const q = this.q.toLowerCase();
            const ev = this.cc.raw.ownership && this.cc.raw.ownership.events ? this.cc.raw.ownership.events : (this.cc.raw.events || []);
            const list = ev || [];
            return list.slice().sort((a, b) => this.fmDate(b.date) - this.fmDate(a.date)).filter((e) => {
                const s = `${this.fmtDate(e.date)} ${e.description} ${e.type || ''} ${e.category || ''}`.toLowerCase();
                return s.includes(q);
            });
        },
    },
    methods: {
        async getCreditCheck() {
            this.busy_loading = true;
            let creditCheckId = this.id;
            if (!creditCheckId) {
                await apiClient.get('/vue/creditchecks?per_page=1&sort_by_column=created_at&sort_by_order=desc&no_old=1&registration_number=' + this.registrationNumber)
                    .then((response) => {
                        if (Array.isArray(response.data.data) && response.data.data.length > 0) {
                            creditCheckId = response.data.data[0].id;
                        }
                    });
            }
            if (creditCheckId) {
                apiClient.get('/vue/creditchecks/' + creditCheckId)
                    .then((response) => {
                        this.cc = response.data;
                    })
                    .finally(() => {
                        this.busy_loading = false;
                    });
            } else {
                this.busy_loading = false;
            }
        },
        ratingBadgeClass(code) {
            const c = (code || '').toUpperCase();
            if (['AAA'].includes(c)) return 'badge-success';
            if (['AA', 'A'].includes(c)) return 'badge-primary';
            if (['B'].includes(c)) return 'badge-warning';
            if (['C', 'AN', '-'].includes(c)) return 'badge-danger';
            return 'badge-danger';
        },
        labelPart(k) {
            const map = {
                abilityToPay: 'Betalningsförmåga',
                finance: 'Finans',
                historyOperation: 'Historik',
                ownership: 'Ägarbild',
            };
            return map[k] || k;
        },
        vAddr(s) { if (!s) return '-'; const p = [s.street, s.postalCode, s.town].filter(Boolean); return p.join(', '); },
        fmtDate(d) {
            if (!d) return '-'; const y = d.year; const m = d.month || 1; const
                day = d.day || 1; const dt = new Date(y, m - 1, day); return dt.toLocaleDateString('sv-SE');
        },
        fmtYearMonth(d) {
            if (!d) return '-'; const y = d.year; const
                m = d.month || 1; const dt = new Date(y, m - 1, 1); return dt.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short' });
        },
        fmtAmount(a) { if (!a) return '-'; const n = Number(a.amount || a); return `${n.toLocaleString('sv-SE')} ${a.currency || 'SEK'}`; },
        fmtNumber(n) { const x = Number(n || 0); return x.toLocaleString('sv-SE'); },
        fmDate(d) { return new Date(d.year, (d.month || 1) - 1, d.day || 1).getTime(); },
        fmStatus(s) {
            const map = {
                ACTIVE: 'Aktivt',
                INACTIVE: 'Inaktivt',
                NOTSTARTED_OR_DORMANT: 'Ej startat eller vilande',
            };
            return map[s.value] || s.value;
        },
        fmLegalForm(lf) {
            const map = {
                LIMITED_COMPANY: 'Aktiebolag',
                SOLE_PROPRIETORSHIP: 'Enskild näringsverksamhet',
                PARTNERSHIP: 'Handelsbolag/Kommanditbolag',
                PERSON: 'Person',
                OTHER: 'Annat',
            };
            return map[lf] || map[lf.code] || lf.description;
        },
        fmLegalFormLocalCode(lf) {
            const map = {
                10: 'Fysisk person',
                21: 'Enkelt bolag',
                22: 'Partrederi',
                23: 'Värdepappersfond',
                31: 'Handelsbolag och kommanditbolag',
                32: 'Gruvbolag',
                41: 'Bankaktiebolag',
                42: 'Försäkringsaktiebolag',
                43: 'Europabolag (SE)',
                49: 'Övrigt aktiebolag',
                51: 'Ekonomisk förening',
                52: 'Föreningsbank',
                53: 'Bostadsrättsförening',
                54: 'Kooperativ hyresrättsförening',
                55: 'Europakooperativ',
                61: 'Ideell förening',
                62: 'Samfällighetsförening, sambruksförening och vägförening',
                63: 'Registrerat trossamfund',
                71: 'Familjestiftelse',
                72: 'Övrig stiftelse och fond',
                81: 'Statlig enhet (myndighet)',
                82: 'Borgerlig primär-kommun',
                83: 'Kommunalförbund',
                84: 'Landsting',
                85: 'Allmän försäkringskassa',
                87: 'Offentlig korporation och offentlig anstalt',
                88: 'Hypoteksförening',
                89: 'Regional statlig myndighet',
                91: 'Oskiftat dödsbo',
                92: 'Ömsesidigt försäkringsbolag',
                93: 'Sparbank',
                94: 'Understödsförening',
                95: 'Erkänd arbetslöshetskassa',
                96: 'Utländsk juridisk person',
                98: 'Övrig svensk juridisk person enligt särskild lagstiftning',
                99: 'Juridisk form ej utredd',
            };
            return map[lf.localCode] || lf.localCode;
        },
    },
};
</script>

<template>
    <div>
        <i v-if="busy_loading" class="icon-spinner2 spinner"></i>
        <div v-else-if="cc">
            <div class="row">
                <!-- Bolagsinformation -->
                <div class="col-12 col-xl-7">
                    <div class="card mb-4">
                        <div class="card-header header-elements-inline bg-white">
                            <h5 class="card-title">Bolagsinformation</h5>
                        </div>
                        <div class="card-body">
                            <h5>{{ cc.raw.companyInformation.companyName.registeredName.name }}</h5>
                            <p>{{ cc.raw.companyInformation.generalCompanyData.businessDescription }}</p>
                            <div class="card">
                                <table class="table table-xs mb-0">
                                    <tbody>
                                        <tr>
                                            <th>Org.nummer</th>
                                            <td>{{ cc.raw.companyInformation.identifiers.registrationNumber }}</td>
                                            <th>Säte</th>
                                            <td>{{ cc.raw.companyInformation.generalCompanyData.location }}</td>
                                        </tr>
                                        <tr>
                                            <th>Status</th>
                                            <td>{{ fmStatus(cc.raw.companyInformation.status) }}</td>
                                            <th>Arbetsgivare</th>
                                            <td style="padding-top: 2px; padding-bottom: 1.5px;">
                                                <span class="badge" :class="cc.raw.companyInformation.generalCompanyData.registeredAsEmployer ? 'badge-success':'badge-secondary'">
                                                    {{ cc.raw.companyInformation.generalCompanyData.registeredAsEmployer ? 'Ja' : 'Nej' }}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Registrerat</th>
                                            <td>{{ fmtDate(cc.raw.companyInformation.registrationInformation.foundationDate) }}</td>
                                            <th>Momsregistrerad</th>
                                            <td style="padding-top: 2px; padding-bottom: 1.5px;">
                                                <span class="badge" :class="cc.raw.companyInformation.generalCompanyData.registeredInVatList ? 'badge-success':'badge-secondary'">
                                                    {{ cc.raw.companyInformation.generalCompanyData.registeredInVatList ? 'Ja' : 'Nej' }}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Bolagsform</th>
                                            <td>{{ fmLegalForm(cc.raw.companyInformation.legalForm) }} ({{ fmLegalFormLocalCode(cc.raw.companyInformation.legalForm) }})</td>
                                            <th>F-skatt</th>
                                            <td style="padding-top: 2px; padding-bottom: 1.5px;">
                                                <span class="badge" :class="cc.raw.companyInformation.generalCompanyData.corporateTax ? 'badge-success':'badge-secondary'">
                                                    {{ cc.raw.companyInformation.generalCompanyData.corporateTax ? 'Ja' : 'Nej' }}
                                                </span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <th>Bransch (SNI)</th>
                                            <td>{{ cc.raw.companyInformation.industryCodeSet.sni2007IndustryCodes.primaryIndustryCode.code }}
                                                – {{ cc.raw.companyInformation.industryCodeSet.sni2007IndustryCodes.primaryIndustryCode.description }}
                                            </td>
                                            <th>Antal anställda</th>
                                            <td>
                                                {{ cc.raw.companyInformation.generalCompanyData.employeeCount }} st
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <h6 class="font-weight-semibold">Postadress</h6>
                                    <div v-if="cc.raw.companyInformation.contactPoints.registeredAddress">
                                        {{ cc.raw.companyInformation.contactPoints.registeredAddress.streetAddress.street }}<br>
                                        {{ cc.raw.companyInformation.contactPoints.registeredAddress.streetAddress.postalCode }}
                                        {{ cc.raw.companyInformation.contactPoints.registeredAddress.streetAddress.town }}<br>
                                        {{ cc.raw.companyInformation.contactPoints.registeredAddress.streetAddress.countryCode }}
                                    </div>
                                </div>
                                <div class="col-12 col-md-6">
                                    <h6 class="font-weight-semibold mb-1">Besöksadress</h6>
                                    <div v-if="cc.raw.companyInformation.contactPoints.visitingAddress">
                                        {{ cc.raw.companyInformation.contactPoints.visitingAddress.streetAddress.street }}<br>
                                        {{ cc.raw.companyInformation.contactPoints.visitingAddress.streetAddress.postalCode }}
                                        {{ cc.raw.companyInformation.contactPoints.visitingAddress.streetAddress.town }}<br>
                                        {{ cc.raw.companyInformation.contactPoints.visitingAddress.streetAddress.countryCode }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Risk & Rating -->
                <div class="col-12 col-xl-5">
                    <div class="card">
                        <div class="card-header header-elements-inline bg-white">
                            <h5 class="card-title">Aktuellt kreditbetyg</h5>
                            <div class="header-elements">
                                <span
                                    v-if="currentRating"
                                    class="badge badge-rating"
                                    :class="ratingBadgeClass(currentRating.code)"
                                    style="font-size: 1.0625rem;"
                                >{{ currentRating.code }}</span>
                                <span
                                    v-else
                                    class="badge badge-rating badge-danger"
                                    style="font-size: 1.0625rem;"
                                >N/A</span>
                            </div>
                        </div>
                        <div v-if="currentRating" class="card-body">
                            <p class="mb-2 text-muted">{{ currentRating.description }}</p>
                            <div class="row">
                                <div class="col-12 col-md-6" v-for="(p,k) in currentRating.partJudgements" :key="k">
                                    <div class="mb-1">{{ labelPart(k) }}</div>
                                    <div class="progress">
                                        <div class="progress-bar" role="progressbar" :style="{width: (p.value/p.max*100).toFixed(0)+'%'}">
                                            <span>{{ p.value }} av {{ p.max }}</span>
                                        </div>
                                    </div>
                                    <div class="small text-muted mt-1">{{ p.description }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row text-center">
                                <div class="col-4">
                                    <div class="mini-stat">
                                        <i class="icon-cash3"></i>
                                        <div>
                                            <div class="text-muted small">Kreditlimit</div>
                                            <div v-if="cc.raw.risk.ratingCreditLimits" class="font-weight-semibold">{{ fmtAmount(cc.raw.risk.ratingCreditLimits.currentRatingCreditLimit.amount) }}</div>
                                            <div v-else class="font-weight-semibold">-</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-4">
                                    <div class="mini-stat">
                                        <i class="icon-statistics"></i>
                                        <div id="paymentindex-score">
                                            <div class="text-muted small">Betalningsindex</div>
                                            <div v-if="cc.raw.risk.trade" class="font-weight-semibold">{{ cc.raw.risk.trade.paymentIndex.currentPaymentIndex.paymentIndex }}</div>
                                            <div v-else class="font-weight-semibold">-</div>
                                        </div>
                                    </div>
                                    <b-popover v-if="cc.raw.risk.trade" target="paymentindex-score" triggers="hover">
                                        <table class="table table-xs mb-0">
                                            <tbody>
                                                <tr>
                                                    <th>Genomsnitt alla bolag</th>
                                                    <td>{{ cc.raw.risk.trade.paymentIndex.currentPaymentIndex.paymentIndexAllCompanies }}</td>
                                                </tr>
                                                <tr>
                                                    <th>Genomsnittlig försening</th>
                                                    <td>{{ cc.raw.risk.trade.paymentIndex.currentPaymentIndex.averageDelay }} dag</td>
                                                </tr>
                                                <tr>
                                                    <th>Underlag</th>
                                                    <td>{{ cc.raw.risk.trade.paymentIndex.currentPaymentIndex.invoiceCount }} fakturor ({{ cc.raw.risk.trade.paymentIndex.currentPaymentIndex.invoiceSuppliersCount }} leverantörer)</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </b-popover>
                                </div>
                                <div class="col-4">
                                    <div class="mini-stat">
                                        <i class="icon-warning2"></i>
                                        <div id="failure-score">
                                            <div class="text-muted small">Failure score</div>
                                            <div class="font-weight-semibold">{{ cc.raw.risk.failureScores.currentScore.scoreValue }} ({{ (parseFloat(cc.raw.risk.failureScores.currentScore.riskPercentage)||0).toFixed(1) }}% risk)</div>
                                        </div>
                                    </div>
                                    <b-popover target="failure-score" triggers="hover">
                                        <table class="table table-xs mb-0">
                                            <tbody>
                                                <tr>
                                                    <th>Datum</th>
                                                    <td>{{ fmtDate(cc.raw.risk.failureScores.currentScore.date) }}</td>
                                                </tr>
                                                <tr>
                                                    <th>Score</th>
                                                    <td>{{ cc.raw.risk.failureScores.currentScore.scoreValue }}</td>
                                                </tr>
                                                <tr>
                                                    <th>Percentil</th>
                                                    <td>{{ cc.raw.risk.failureScores.currentScore.percentileScore }}</td>
                                                </tr>
                                                <tr>
                                                    <th>PD (12m)</th>
                                                    <td>{{ (parseFloat(cc.raw.risk.failureScores.currentScore.probabilityOfDefault)*100).toFixed(2) }}%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                        <div
                                            v-if="Array.isArray(cc.raw.risk.failureScores.currentScore.influencingFactors) && cc.raw.risk.failureScores.currentScore.influencingFactors.length > 0"
                                            class="mt-3"
                                        >
                                            <h6 class="mb-2">Påverkande faktorer</h6>
                                            <ol class="mb-0">
                                                <li v-for="f in cc.raw.risk.failureScores.currentScore.influencingFactors" :key="f.negativeRank">
                                                    {{ f.description }}
                                                </li>
                                            </ol>
                                        </div>
                                    </b-popover>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Nyckeltal -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default6">Nyckeltal</a>
                    </h5>
                    <div class="header-elements">
                        <div v-if="Object.keys(keyFiguresSorted).length > 0" class="badge badge-secondary">{{ keyFiguresSorted.length }}</div>
                        <div v-else class="badge badge-secondary">N/A</div>
                    </div>
                </div>
                <div v-if="Object.keys(keyFiguresSorted).length > 0" id="collapse-item-default6" class="collapse show">
                    <div class="card-body">
                        <table class="table table-sm align-middle mb-0">
                            <tbody>
                                <tr>
                                    <th>År</th>
                                    <th
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-closingPeriod-' + ki"
                                    >
                                        {{ k.closingPeriod.year }}
                                    </th>
                                </tr>
                                <tr>
                                    <th>Omsättning</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-turnover-' + ki"
                                    >
                                        {{ fmtNumber(k.turnover) }} {{ k.currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Omsättningsförändring</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-growthInTurnover-' + ki"
                                    >
                                        {{ k.growthInTurnover }}%
                                    </td>
                                </tr>
                                <tr>
                                    <th>Nettoresultat</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-netMargin-' + ki"
                                    >
                                        {{ fmtNumber(k.netMargin) }} {{ k.currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Resultat efter finansnetto</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-resultAfterFinancialItems-' + ki"
                                    >
                                        {{ fmtNumber(k.resultAfterFinancialItems) }} {{ k.currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Rörelsemarginal</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-profitMargin-' + ki"
                                    >
                                        {{ k.profitMargin }}%
                                    </td>
                                </tr>
                                <tr>
                                    <th>Kassalikviditet</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-quickRatio-' + ki"
                                    >
                                        {{ k.quickRatio }}%
                                    </td>
                                </tr>
                                <tr>
                                    <th>Soliditet</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-equityAssetsRatio-' + ki"
                                    >
                                        {{ k.equityAssetsRatio }}%
                                    </td>
                                </tr>
                                <tr>
                                    <th>Skuldsättningsgrad</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-liabilityRatio-' + ki"
                                    >
                                        {{ k.liabilityRatio }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Avkastning eget kapital</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-returnOnEquity-' + ki"
                                    >
                                        {{ k.returnOnEquity }}%
                                    </td>
                                </tr>
                                <tr>
                                    <th>Avkastning totalt kapital</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-returnOnTotalAssets-' + ki"
                                    >
                                        {{ k.returnOnTotalAssets }}%
                                    </td>
                                </tr>
                                <tr>
                                    <th>Eget kapital</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-equity-' + ki"
                                    >
                                        {{ fmtNumber(k.equity) }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Summa tillgångar</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-assetsTotal-' + ki"
                                    >
                                        {{ fmtNumber(k.assetsTotal) }} {{ k.currency }}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Antal anställda</th>
                                    <td
                                        v-for="(k, ki) in keyFiguresSorted"
                                        :key="'kFS-employeeCount-' + ki"
                                    >
                                        {{ k.employeeCount }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Betalnningsanmärkningar -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default10">Betalningsanmärkningar</a>
                    </h5>
                    <div class="header-elements">
                        <div
                            class="badge"
                            :class="[cc.raw.risk.paymentRemarks.count > 0 ? 'badge-danger' : 'badge-secondary']"
                        >{{ cc.raw.risk.paymentRemarks.count }}</div>
                    </div>
                </div>
                <div id="collapse-item-default10" class="collapse">
                    <div class="card-body">
                        <div v-if="cc.raw.risk.paymentRemarks.count > 0">
                            <div>
                                {{ cc.raw.risk.paymentRemarks.count }} st betalningsanmärkningar
                                <div>{{ fmtAmount(cc.raw.risk.paymentRemarks.amount) }}</div>
                            </div>
                            <table class="table table-sm mb-0 align-middle">
                                <thead>
                                <tr>
                                    <th>Datum</th>
                                    <th>Belopp</th>
                                    <th>Fordringsägare</th>
                                    <th>Beskrivning</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="pr in cc.raw.risk.paymentRemarks.paymentRemarks"
                                    :key="fmtDate(pr.date)+pr.amount"
                                >
                                    <td>{{ fmtDate(pr.date) }}</td>
                                    <td>{{ fmtAmount(pr.amount) }}</td>
                                    <td>{{ pr.creditor.name }}</td>
                                    <td>{{ pr.description }}</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else>
                            <i>Inga betalningsanmärkningar</i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Adresser -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">Adresser</h5>
                    <div class="header-elements">
                        <div class="badge badge-secondary">{{ cc.raw.companyInformation.branches.branches.length }}</div>
                    </div>
                </div>
                <div class="card-body px-2 pt-2 pb-0">
                    <div v-if="cc.raw.companyInformation.branches.branches.length > 0" class="row">
                        <div
                            v-for="(b, bI) in cc.raw.companyInformation.branches.branches"
                            :key="'b-' + bI"
                            class="col-12 col-lg-6 col-xl-4 col-xxl-3"
                        >
                            <div class="card card-body d-flex flex-row card-body p-2 mb-2">
                                <div class="flex-grow-1">
                                    {{ b.address.street }}<br>
                                    {{ b.address.postalCode }}
                                    {{ b.address.town }}<br>
                                    {{ b.address.countryCode }}
                                </div>
                                <div v-if="b.headQuarter" class="d-flex align-items-center">
                                    <div class="badge badge-success">HK</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <i v-else class="mb-3">Inga adresser</i>
                </div>
            </div>

            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default9">Bankgiro</a>
                    </h5>
                    <div class="header-elements">
                        <div class="badge badge-secondary">{{ cc.raw.companyInformation.bankgiroAccounts.length }}</div>
                    </div>
                </div>
                <div id="collapse-item-default9" class="collapse">
                    <div class="card-body px-2 pt-2 pb-0">
                        <div v-if="cc.raw.companyInformation.bankgiroAccounts.length > 0" class="row">
                            <div
                                v-for="(ba, baI) in cc.raw.companyInformation.bankgiroAccounts"
                                :key="'ba-' + baI"
                                class="col-12 col-lg-6 col-xl-4 col-xxl-3"
                            >
                                <div class="card card-body d-flex flex-row p-2 mb-2">
                                    <div class="flex-grow-1">
                                        <div class="font-weight-semibold">{{ ba.bankgiroNumber }}</div>
                                        <div>{{ ba.name }}</div>
                                        <div>{{ ba.address.postalCode }}, {{ ba.address.town }}</div>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <div class="badge" :class="{ 'badge-success': ba.valid, 'badge-danger': !ba.valid }">{{ba.valid ? 'AKTIV' : 'EJ AKTIV' }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <i v-else class="mb-3">Inga bankgiron</i>
                    </div>
                </div>
            </div>

            <!-- Koncernträd -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default8">Koncernträd</a>
                    </h5>
                </div>
                <div id="collapse-item-default8" class="collapse">
                    <div class="card-body">
                        <company-tree v-if="cc.raw.ownership && cc.raw.ownership.familyTree && cc.raw.ownership.familyTree.ultimateParent" :root="cc.raw.ownership.familyTree.ultimateParent" />
                        <i v-else>Koncernträd ej tillgängligt</i>
                    </div>
                </div>
            </div>

            <!-- Kreditbetyg historik -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default1">Kreditbetyg (historik)</a>
                    </h5>
                    <div class="header-elements">
                        <div v-if="historicalCreditRatings.length > 0" class="badge badge-secondary">{{ historicalCreditRatings.length }}</div>
                        <div v-else class="badge badge-secondary">N/A</div>
                    </div>
                </div>
                <div v-if="historicalCreditRatings.length > 0" id="collapse-item-default1" class="collapse">
                    <div class="card-body">
                        <table class="table table-sm mb-0 align-middle">
                            <thead>
                                <tr>
                                    <th>Datum</th>
                                    <th>Årsredovisning</th>
                                    <th>Betyg</th>
                                    <th>Beskrivning</th>
                                    <th>Belopp</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="r in historicalCreditRatings" :key="fmtDate(r.date)+r.code">
                                    <td>{{ fmtDate(r.date) }}</td>
                                    <td>{{ fmtYearMonth(r.annualReportDate) }}</td>
                                    <td><span class="badge" :class="ratingBadgeClass(r.code)">{{ r.code }}</span></td>
                                    <td>{{ r.description }}</td>
                                    <td>{{ fmtAmount(r.amount) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Ledning & revisorer -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default5">Ledning & revisorer</a>
                    </h5>
                    <div class="header-elements">
                        <div
                            v-if="cc.raw.management
                            && (cc.raw.management.boardMembers && cc.raw.management.boardMembers.currentBoardMembers)
                            || (cc.raw.management.auditors && cc.raw.management.auditors.currentAuditors)"
                            class="badge badge-secondary"
                        >
                            {{ (cc.raw.management.boardMembers && cc.raw.management.boardMembers.currentBoardMembers ? cc.raw.management.boardMembers.currentBoardMembers.length : 0)
                            + (cc.raw.management.auditors && cc.raw.management.auditors.currentAuditors ? cc.raw.management.auditors.currentAuditors.length : 0) }}
                        </div>
                        <div v-else class="badge badge-secondary">
                            N/A
                        </div>
                    </div>
                </div>
                <div
                    v-if="cc.raw.management
                    && (cc.raw.management.boardMembers && cc.raw.management.boardMembers.currentBoardMembers)
                    || (cc.raw.management.auditors && cc.raw.management.auditors.currentAuditors)"
                    id="collapse-item-default5"
                    class="collapse"
                >
                    <div class="card-body">
                        <div class="row">
                            <div v-if="cc.raw.management.boardMembers && cc.raw.management.boardMembers.currentBoardMembers" class="col-12 col-lg-6">
                                <h6>Styrelse</h6>
                                <div class="card">
                                    <table class="table table-sm align-middle mb-0">
                                        <tbody>
                                            <tr
                                                v-for="m in cc.raw.management.boardMembers.currentBoardMembers"
                                                :key="'bm-' + m.entity.registrationNumber"
                                            >
                                                <td>
                                                    <div class="font-weight-semibold">{{ m.entity.name }}</div>
                                                    <div class="small text-muted">{{ m.description }} · sedan {{ fmtDate(m.enteredDate) }}</div>
                                                </td>
                                                <td class="text-right">
                                                    <div class="text-muted text-uppercase">{{ fmLegalForm(m.type) }}</div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div v-if="cc.raw.management.auditors && cc.raw.management.auditors.currentAuditors" class="col-12 col-lg-6">
                                <h6>Revisorer</h6>
                                <div class="card">
                                    <table class="table table-sm align-middle mb-0">
                                        <tbody>
                                            <tr
                                                v-for="m in cc.raw.management.auditors.currentAuditors"
                                                :key="'bm-' + m.entity.registrationNumber"
                                            >
                                                <td>
                                                    <div class="font-weight-semibold">{{ m.entity.name }}</div>
                                                    <div class="small text-muted">{{ m.description }} · sedan {{ fmtDate(m.enteredDate) }}</div>
                                                </td>
                                                <td class="text-right">
                                                    <div class="text-muted text-uppercase">{{ fmLegalForm(m.type) }}</div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Händelser -->
            <div class="card">
                <div class="card-header header-elements-inline bg-white">
                    <h5 class="card-title">
                        <a data-toggle="collapse" class="text-body" href="#collapse-item-default7">Händelser</a>
                    </h5>
                    <div class="header-elements">
                        <div class="badge badge-secondary">
                            {{ filteredEvents.length }}
                        </div>
                    </div>
                </div>

                <div id="collapse-item-default7" class="collapse">
                    <div class="card-body">
                        <div class="timeline">
                            <div class="t" v-for="e in filteredEvents" :key="fmtDate(e.date)+e.description">
                                <div class="font-weight-semibold">{{ fmtDate(e.date) }}</div>
                                <div>{{ e.description }}</div>
                                <div class="small text-muted" v-if="e.category">Kategori: {{ e.category }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <div class="alert alert-info">Ingen up-to-date kreditkontroll tillgänglig</div>
        </div>
    </div>
</template>

<style scoped>

</style>
